import { useContext, useMemo, useCallback } from 'react';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { generateShiftName } from '@ubeya/shared/utils/scheduling';
import { getLeafChildrenData } from 'timeSheet/utils';
import { ShiftAggregateRenderer, ShiftRenderer } from 'timeSheet/components/CellRenderers';

const useShiftColumn = ({ accountId, mappedLabels, mappedJobTypes, mappedLocations, mappedPtoTypes }) => {
  const { t } = useContext(ConfigContext);

  const valueGetter = useCallback(
    (params) => {
      if (params.node?.group) {
        const groupItems = getLeafChildrenData(params.node);
        return groupItems.length;
      }
      return (
        params.data?.shiftId ||
        generateShiftName(params.data, mappedJobTypes, mappedLabels, mappedLocations, mappedPtoTypes) ||
        ''
      );
    },
    [mappedJobTypes, mappedLabels, mappedLocations, mappedPtoTypes]
  );

  const comparator = useCallback(
    (_valueA, _valueB, nodeA, nodeB) => {
      if (nodeA?.group || nodeB?.group) {
        return 0;
      }

      const nameA = generateShiftName(nodeA.data, mappedJobTypes, mappedLabels, mappedLocations, mappedPtoTypes) || '';
      const nameB = generateShiftName(nodeB.data, mappedJobTypes, mappedLabels, mappedLocations, mappedPtoTypes) || '';
      return nameA.localeCompare(nameB);
    },
    [mappedJobTypes, mappedLabels, mappedLocations, mappedPtoTypes]
  );

  const cellRenderer = useCallback(
    (params) => {
      if (params.node?.group) {
        const groupItems = getLeafChildrenData(params.node);
        return ShiftAggregateRenderer({ rowData: { shift: groupItems.length } });
      }

      return ShiftRenderer({
        rowData: params.data,
        columnData: {
          accountId,
          mappedLabels,
          mappedJobTypes,
          mappedLocations,
          mappedPtoTypes
        }
      });
    },
    [accountId, mappedLabels, mappedJobTypes, mappedLocations, mappedPtoTypes]
  );

  return useMemo(
    () => ({
      field: 'shift',
      headerName: t('shift'),
      cellClass: 'justify-start',
      width: 200,
      valueGetter,
      comparator,
      cellRenderer
    }),
    [t, valueGetter, comparator, cellRenderer]
  );
};

export default useShiftColumn;
